import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import CyberIntelModal from "../components/CyberIntelModal";
import { SeoHead } from "../components/SeoHead";
import { ShieldAlert, Loader2, Sparkles, ExternalLink, Filter, TrendingUp, AlertTriangle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRACKERS = [
  { key: "all",            label: "All cyber",                 emoji: "🛡️" },
  { key: "ransomware",     label: "Ransomware",                emoji: "🔒" },
  { key: "data_breach",    label: "Data breach",               emoji: "📂" },
  { key: "ai_scam",        label: "AI scams",                  emoji: "🎭" },
  { key: "crypto_theft",   label: "Crypto theft",              emoji: "₿"  },
  { key: "gov_vuln",       label: "Government vulnerability",  emoji: "🏛️" },
  { key: "banking_fraud",  label: "Banking fraud",             emoji: "🏦" },
  { key: "identity_theft", label: "Identity theft",            emoji: "🪪" },
  { key: "consumer_safety", label: "Consumer safety",          emoji: "📞" },
  { key: "small_business",  label: "Small business",           emoji: "🏢" },
];

export default function CyberCenter() {
  const [tracker, setTracker] = useState("all");
  const [feed, setFeed] = useState([]);
  const [bands, setBands] = useState([]);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(true);
  const [intelFor, setIntelFor] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setBusy(true);
    const url = tracker === "all" ? `${API}/cyber/feed` : `${API}/cyber/trackers/${tracker}`;
    Promise.all([
      axios.get(url, { params: { limit: 24 }, timeout: 12000 }),
      axios.get(`${API}/cyber/stats`, { timeout: 12000 }).catch(() => null),
    ]).then(([f, s]) => {
      if (cancelled) return;
      setFeed(f.data.feed || []);
      setBands(f.data.bands || s?.data?.bands || []);
      if (s?.data) setStats(s.data);
    }).catch(() => {})
      .finally(() => { if (!cancelled) setBusy(false); });
    return () => { cancelled = true; };
  }, [tracker]);

  return (
    <>
      <SeoHead
        title="Cyber Intelligence Center — Threats, Scored"
        description="Breaking cybersecurity news rated for what you should do about it. Severity scores, live threat trackers, and consumer + business protection intel."
      />
      <Header />
      <main className="container mx-auto max-w-[1320px] px-4 py-6" data-testid="cyber-center">
        {/* Hero */}
        <section className="rounded-2xl border border-[var(--line)] bg-gradient-to-br from-[#0a1f3d] via-[#0f2a5f] to-[#1d1303] text-white p-6 mb-5 relative overflow-hidden">
          <div className="absolute top-4 right-4 opacity-10">
            <ShieldAlert size={140} />
          </div>
          <div className="relative">
            <div className="text-[11px] uppercase tracking-[0.22em] font-black text-[var(--gold)] mb-1">
              Newseze Cyber Intelligence Center
            </div>
            <h1 className="text-[26px] sm:text-[34px] font-black tracking-[-0.02em] leading-tight mb-2">
              Breaking cybersecurity news · rated for what you should do about it.
            </h1>
            <p className="text-[13.5px] opacity-85 max-w-2xl leading-snug">
              Every story scored 1–100 for urgency. Five severity bands. Ten plain-English fields per article. No
              hype, no AI mystique — just Newseze's algorithm reading Krebs, Dark Reading, BleepingComputer,
              SecurityWeek, The Hacker News and CISA so you don't have to.
            </p>
          </div>
        </section>

        {/* Severity legend */}
        {bands.length > 0 && (
          <section className="rounded-xl border border-[var(--line)] bg-white p-3 mb-4" data-testid="cyber-severity-legend">
            <div className="text-[10px] uppercase tracking-[0.18em] font-black text-[var(--muted)] mb-2 flex items-center gap-1">
              <Filter size={11} /> Severity scale
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
              {bands.map((b) => (
                <div key={b.key} className="flex items-start gap-2 p-2 rounded-md border" style={{ borderColor: b.color + "55" }} data-testid={`severity-band-${b.key}`}>
                  <div className="w-9 h-9 rounded-md flex items-center justify-center text-white text-[14px] font-black shrink-0" style={{ backgroundColor: b.color }}>
                    {b.icon}
                  </div>
                  <div className="min-w-0">
                    <div className="text-[12px] font-black" style={{ color: b.color }}>{b.label}</div>
                    <div className="text-[10.5px] text-[var(--muted)] leading-snug">{b.action}</div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Stats strip */}
        {stats && (
          <section className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            <StatTile label="Cyber stories" value={stats.total} accent="#0a1f3d" />
            <StatTile label="Last 24h" value={stats.in_24h} accent="#dc2626" />
            <StatTile label="Last 7 days" value={stats.in_7d} accent="#f97316" />
            <StatTile label="Critical (intel'd)" value={(stats.severity_distribution?.critical ?? 0) + (stats.severity_distribution?.high ?? 0)} accent="#000" />
          </section>
        )}

        {/* Tracker chips */}
        <section className="mb-4" data-testid="cyber-trackers">
          <div className="text-[10px] uppercase tracking-[0.16em] font-black text-[var(--muted)] mb-2">Trackers · filter by threat type</div>
          <div className="flex flex-wrap gap-2">
            {TRACKERS.map((t) => {
              const cnt = stats?.threat_type_counts?.[t.key];
              const active = tracker === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => setTracker(t.key)}
                  className={`px-3 py-1.5 rounded-full text-[12px] font-black border transition ${active
                    ? "bg-[var(--ink)] text-white border-[var(--ink)]"
                    : "bg-white text-[var(--ink)] border-[var(--line)] hover:border-[var(--ink)]"}`}
                  data-testid={`cyber-tracker-${t.key}`}
                >
                  <span className="mr-1">{t.emoji}</span>{t.label}
                  {typeof cnt === "number" && t.key !== "all" && <span className="ml-1.5 opacity-70">· {cnt}</span>}
                </button>
              );
            })}
          </div>
        </section>

        {/* Feed */}
        <section data-testid="cyber-feed">
          {busy && (
            <div className="text-[var(--muted)] flex items-center gap-2 text-[13px] py-6">
              <Loader2 size={14} className="animate-spin" /> Loading cyber feed…
            </div>
          )}
          {!busy && feed.length === 0 && (
            <div className="rounded-lg border border-[var(--line)] p-6 text-center text-[var(--muted)] text-[13px]">
              No cyber stories match this tracker right now. Check back soon.
            </div>
          )}
          {!busy && feed.length > 0 && (
            <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {feed.map((a) => (
                <CyberCard key={a.id} a={a} onIntel={() => setIntelFor(a.id)} />
              ))}
            </ul>
          )}
        </section>

        <p className="text-[10.5px] text-[var(--muted)] italic mt-6 border-t border-[var(--line)] pt-3">
          Newseze's algorithm processes feeds from Krebs on Security, Dark Reading, BleepingComputer, SecurityWeek,
          The Hacker News, CISA, and US-CERT under fair use (17 U.S.C. § 107). Intel briefings are for awareness only — not
          a substitute for security advice from a qualified professional.
        </p>
      </main>
      <Footer />

      {intelFor && <CyberIntelModal articleId={intelFor} onClose={() => setIntelFor(null)} />}
    </>
  );
}

function StatTile({ label, value, accent }) {
  return (
    <div className="rounded-xl border border-[var(--line)] bg-white p-3" data-testid={`cyber-stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-[0.16em] font-black" style={{ color: accent }}>{label}</div>
      <div className="text-[24px] font-black tracking-tight mt-1">{value ?? "—"}</div>
    </div>
  );
}

function CyberCard({ a, onIntel }) {
  const band = a.severity_band;
  const thr = (a.threat_type || "general").replace(/_/g, " ");
  return (
    <li className="rounded-xl border border-[var(--line)] bg-white overflow-hidden" data-testid={`cyber-card-${a.id}`}>
      <div className="p-4">
        <div className="flex items-center gap-2 mb-2">
          {band ? (
            <span className="text-[10.5px] uppercase tracking-[0.14em] font-black px-2 py-1 rounded text-white" style={{ backgroundColor: band.color }} data-testid={`cyber-card-band-${a.id}`}>
              {band.icon} {band.label} · {a.severity_score}
            </span>
          ) : (
            <span className="text-[10.5px] uppercase tracking-[0.14em] font-black px-2 py-1 rounded bg-[var(--sky)] text-[var(--ink)]">
              Intel pending
            </span>
          )}
          <span className="text-[10.5px] uppercase tracking-[0.14em] font-black px-2 py-1 rounded bg-[var(--ink)] text-white">
            {thr}
          </span>
          <span className="text-[11px] text-[var(--muted)] ml-auto">{a.source}</span>
        </div>
        <Link to={a.link} className="block">
          <h3 className="text-[16px] font-black leading-tight tracking-[-0.01em] hover:text-[var(--blue)]" data-testid={`cyber-card-title-${a.id}`}>
            {a.title}
          </h3>
        </Link>
        {a.summary && <p className="text-[12.5px] text-[var(--muted)] mt-2 leading-snug line-clamp-3">{a.summary}</p>}
        <div className="flex items-center gap-2 mt-3">
          <button onClick={onIntel} className="btn small primary" data-testid={`cyber-intel-btn-${a.id}`}>
            <Sparkles size={11} /> Generate intel briefing
          </button>
          <Link to={a.link} className="btn small" data-testid={`cyber-read-${a.id}`}>
            <AlertTriangle size={11} /> Read story
          </Link>
          {a.external_link && (
            <a href={a.external_link} target="_blank" rel="nofollow noopener noreferrer" referrerPolicy="no-referrer"
               className="btn small icon ml-auto" title="Open original" data-testid={`cyber-external-${a.id}`}>
              <ExternalLink size={11} />
            </a>
          )}
        </div>
      </div>
    </li>
  );
}
